/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { View, TextInput, StyleSheet } from 'react-native';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';

import { useMultiMoveContext } from '../SpotDesignation';

import { Text, Checkbox } from '../../uiold';

type SortEndDetails = {
  oldIndex: number;
  newIndex: number;
};

type SortStartDetails = {
  index: number;
};

type SortableItemProps = {
  editable: boolean;
  index: number;
  sortableIndex: number;
  isDragged: boolean;
  showHandle: boolean;
  showSeparator: boolean;
  name: string;
  onMouseEnter: (event: any, index: number) => any;
  onMouseLeave: (event: any) => any;
  onPositionNameChange: (index: number, name: string) => void;
};

type SortableListProps = {
  positionMap: Array<string>;
  draggedIndex?: number | null | undefined;
  hoveredIndex?: number | null | undefined;
  isDragging: boolean;
  isEditing: boolean;
  onMouseEnter: (event: any, index: number) => any;
  onMouseLeave: (event: any) => any;
  onPositionNameChange: (index: number, name: string) => void;
};

type DragHandleProps = {
  hidden: boolean;
};

type Props = Omit<
  SortableListProps,
  'distance' | 'lockAxis' | 'lockToContainerEdges'
> & {
  onSort?: (oldIndex: number, newIndex: number) => void;
  onSortEnd?: (
    details: SortEndDetails,
    event: {
      [key: string]: any;
    },
  ) => void;
  onSortStart?: (
    details: SortStartDetails,
    event: {
      [key: string]: any;
    },
  ) => void;
};

export default function SortableDesignation(props: Props) {
  const onSortEnd = (
    details: SortEndDetails,
    event: {
      [key: string]: any;
    },
  ) => {
    let { onSortEnd, onSort } = props;
    if (onSortEnd) {
      onSortEnd(details, event);
    }
    if (onSort) {
      let { oldIndex, newIndex } = details;
      if (oldIndex !== newIndex) {
        onSort(oldIndex, newIndex);
      }
    }
  };

  const onSortStart = (
    details: SortStartDetails,
    event: {
      [key: string]: any;
    },
  ) => {
    let { onSortStart } = props;
    if (onSortStart) {
      onSortStart(details, event);
    }
  };

  return (
    <View style={styles.root}>
      <View style={styles.headerRow}>
        <DragHandle hidden={true} />
        <View style={styles.order}>
          <Text style={styles.headerText}>Order</Text>
        </View>
        <View style={styles.name}>
          <Text style={styles.headerText}>Stall Name</Text>
        </View>
      </View>
      <SortableList
        {...props}
        onSortEnd={onSortEnd}
        onSortStart={onSortStart}
        distance={1}
        lockAxis="y"
        lockToContainerEdges={true}
      />
    </View>
  );
}

let SortableList = SortableContainer((props: SortableListProps) => {
  let {
    onMouseEnter,
    onMouseLeave,
    positionMap,
    draggedIndex,
    hoveredIndex,
    isDragging,
    isEditing,
    onPositionNameChange,
  } = props;
  return (
    <View>
      {positionMap.map((name, index) => {
        let isHovered = hoveredIndex === index;
        let showHandle = isEditing && isHovered;
        let showSeparator = !isEditing && index !== 0;
        return (
          <SortableItem
            key={`item-${index}`}
            index={index}
            sortableIndex={index}
            name={name || (index + 1).toString()}
            onPositionNameChange={onPositionNameChange}
            onMouseEnter={isDragging ? () => {} : onMouseEnter}
            onMouseLeave={onMouseLeave}
            editable={isEditing}
            showHandle={showHandle}
            showSeparator={showSeparator} // helperClass props add className to the element, doesn't work as of now
            // TODO: Try using aphrodite or className instead.
            // helperClass="dragged-position-item"
            isDragged={draggedIndex === index}
          />
        );
      })}
    </View>
  );
});

let SortableItem = SortableElement((props: SortableItemProps) => {
  const moveContext = useMultiMoveContext();

  let {
    onMouseEnter,
    onMouseLeave,
    sortableIndex,
    name,
    editable,
    showHandle,
    showSeparator,
    onPositionNameChange,
  } = props;

  return editable ? (
    <View
      style={[
        showHandle ? styles.hoveredItem : styles.item,
        showSeparator ? styles.itemSeparator : styles.itemHiddenSeparator,
      ]}
      onMouseEnter={(e) => onMouseEnter(e, sortableIndex)}
      onMouseLeave={onMouseLeave}
    >
      <DragHandle hidden={!showHandle} />
      <Checkbox
        isChecked={moveContext.selectedRows.has(sortableIndex)}
        onChange={(value) => {
          moveContext.onToggleSelection(sortableIndex, value);
        }}
      />
      <View style={styles.order}>
        <Text style={styles.orderText}>{sortableIndex + 1}</Text>
      </View>
      <View style={styles.name}>
        <View style={styles.nameInputContainer}>
          <TextInput
            value={name}
            onChangeText={(text) =>
              onPositionNameChange(sortableIndex, text.length > 4 ? name : text)
            }
          />
        </View>
        <Text style={styles.nameLength}>{name.length}/4</Text>
      </View>
    </View>
  ) : (
    <View style={[styles.item, styles.itemSeparator, { paddingLeft: 30 }]}>
      <View style={styles.order}>
        <Text style={styles.orderText}>{sortableIndex + 1}</Text>
      </View>
      <View style={styles.name}>
        <Text style={styles.nameText}>{name}</Text>
      </View>
    </View>
  );
});

let DragHandle = SortableHandle(({ hidden }: DragHandleProps) =>
  hidden ? (
    <View style={styles.handle} />
  ) : (
    <View style={styles.handle}>
      <View style={{ flexDirection: 'row' }}>
        <View style={styles.dot} />
        <View style={styles.dot} />
      </View>
      <View style={{ flexDirection: 'row' }}>
        <View style={styles.dot} />
        <View style={styles.dot} />
      </View>
      <View style={{ flexDirection: 'row' }}>
        <View style={styles.dot} />
        <View style={styles.dot} />
      </View>
      <View style={{ flexDirection: 'row' }}>
        <View style={styles.dot} />
        <View style={styles.dot} />
      </View>
    </View>
  ),
);

let centered: ObjectOf<any> = {
  justifyContent: 'center',
  alignItems: 'center',
};

let item: ObjectOf<any> = {
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: 'white',
  height: 50,
  borderLeftWidth: 3,
  borderLeftColor: 'transparent',
  borderRightWidth: 1,
  borderRightColor: 'transparent',
};

let styles = StyleSheet.create({
  root: {
    borderBottomWidth: 1,
    borderBottomColor: '#e7ebf0',
  },
  item: item,
  hoveredItem: {
    ...item,
    borderLeftColor: '#7aba4c',
    borderRightColor: '#e7ebf0',
    shadowColor: 'black',
    shadowOffset: {
      width: 1,
      height: 1,
    },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 1,
  },
  itemSeparator: {
    borderTopWidth: 1,
    borderTopColor: '#e7ebf0',
  },
  itemHiddenSeparator: {
    borderTopWidth: 1,
    borderTopColor: 'transparent',
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e7ebf0',
    paddingTop: 7,
    paddingBottom: 20,
  },
  headerText: {
    fontWeight: '600',
    color: '#777d85',
  },
  handle: {
    cursor: 'grab',
    alignItems: 'center',
    width: 30,
  } as any,
  dot: {
    width: 2,
    height: 2,
    backgroundColor: '#bcc2ca',
    borderRadius: 1,
    borderWidth: 1,
    borderColor: '#bcc2ca',
    margin: 1,
  },
  order: {
    ...centered,
    width: 60,
    marginRight: 20,
  },
  orderText: {
    fontWeight: '500',
    color: '#bcc2ca',
  },
  name: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  nameText: {
    paddingLeft: 11,
  },
  nameInputContainer: {
    width: 250,
  },
  nameLength: {
    textAlign: 'right',
    minWidth: 40,
    marginLeft: 10,
    fontWeight: '500',
    color: '#bcc2ca',
  },
});
